import { useDashboard } from '@app/routes/Dashboard/useDashboard';
import { AlertDialog, Loading } from '@components/ui';
import { User, UserQuestionnaireApplicationTopic, UserTopicApplications } from '@dieterApi/user/useUserQuery';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { useEntryHighlight } from '@app/hooks/useEntryHighlight';
import MUIDataTable, { MUIDataTableMeta, MUIDataTableOptions, MUIDataTableProps } from 'mui-datatables';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useImmer } from 'use-immer';

import { Topics } from '@app/context/constants';
import { useNavigation } from '@app/hooks/useNavigation';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { copyToClipboard } from '@app/utils/copyToClipboard';
import { getTopicStates } from '@app/utils/getTopicStates';
import { UserValue } from '@dieterApi/persistentState/useUserValuesQuery';
import { ProcessingConsent } from '@dieterApi/questionnaire/useAddProcessingConsentMutation';
import { useQuestionnaireDocumentShareQuery } from '@dieterApi/questionnaire/useQuestionnaireDocumentShareQuery';
import { useWithdrawProcessingConsentMutation } from '@dieterApi/questionnaire/useWithdrawProcessingConsentMutation';
import { useUserValueAddMutation } from '@dieterApi/user/useUserValueAddMutation';
import { useUserValueDeleteMutation } from '@dieterApi/user/useUserValueDeleteMutation';
import { useTranslation } from 'react-i18next';

export interface InfoFormTableMeta extends MUIDataTableMeta {
  rowData: [
    string, // index
    string, // description
    string | null, // createdAt
    UserQuestionnaireApplicationTopic | null,
  ];
}

interface Props {
  topic: UserTopicApplications;
  topicStates: ReturnType<typeof getTopicStates>;
}

export function InfoForms({ topic, topicStates }: Props): JSX.Element {
  const { t } = useTranslation();
  const { user, principalCompany, topics, questionnaires, questionnairesWithoutDpoFilter } = useUserWithCompany();
  const { userValues } = useUserValuesData();
  const dashboard = useDashboard();
  const {
    navigation: { roadBlock },
    setNavigation,
  } = useNavigation();
  const blankEntry = { index: '', description: '', consents: [] };
  const [newEntry, setNewEntry] = useImmer<InfoFormData>(blankEntry);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<InfoFormTableMeta | null>(null);
  const [withdrawConsent] = useWithdrawProcessingConsentMutation();
  const { enqueueSnackbar } = useSnackbar();
  const [getShareToken, { data: tokenData, loading: shareLoading }] = useQuestionnaireDocumentShareQuery();

  // const [activeQuestionnaire, setActiveQuestionnaire] = useState<UserQuestionnaireApplicationTopic | null>(null);
  const infoFormData = useMemo(() => {
    if (!userValues) return [];
    const parsedData = extractInfoFormData(user, userValues, questionnairesWithoutDpoFilter);
    return newEntry.index ? [...parsedData, newEntry] : parsedData;
  }, [user, userValues, newEntry, questionnairesWithoutDpoFilter]);

  const [addUserValue] = useUserValueAddMutation();
  const [deleteUserValue] = useUserValueDeleteMutation();

  // when new input field on manual entry row is added, focus it
  const handleRef = useCallback<(node: HTMLInputElement) => void>((node) => {
    if (node !== null) {
      node.focus();
    } else {
      // clear focus
      window.focus();
      // document.activeElement?.blur();
    }
  }, []);

  const handleDelete = (tableMeta: InfoFormTableMeta) => {
    const index = tableMeta.rowData?.[0] as string;
    if (index === 'new') return;
    const keys = ['INFOFORM_DESCRIPTION'];
    keys.forEach((key) => deleteUserValue({ variables: { key, index }, context: { userId: user?.id || '' } }));
  };

  const handleAdd = () => {
    addUserValue({
      variables: { key: 'INFOFORM_DESCRIPTION', value: '', createIndex: true },
      // context: { userId: user?.id || '' },
    }).then((res) => {
      if (res.data?.addUserValue) {
        const app = topics.find((t) => t.id === Topics.INFO)?.applications.find((a) => a.id === 'app-info-create-new');
        app && dashboard.create(app.id, undefined, true, res.data.addUserValue.index);
      }
    });
  };

  const handleUpdate = (infoForm: InfoFormData, description: string) => {
    addUserValue({
      variables: { key: 'INFOFORM_DESCRIPTION', value: description, index: infoForm.index },
    });
  };

  const handleOpenQuestionnaire = (meta: InfoFormTableMeta) => {
    dashboard.open(meta.rowData[3]!.id, undefined, true);
  };

  const handleShowContract = (
    meta: InfoFormTableMeta,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    action: 'show' | 'download'
  ) => {
    if (topicStates.isLocked) {
      roadBlock.open(topic);
    }
    const questionnaire = meta.rowData[3]!;

    setNavigation((nav) => {
      nav.documentPaper = {
        modalOpen: true,
        questionnaire,
        index: 0,
      };
    });
    // setActiveQuestionnaire(meta.rowData[4]!);
    // setAnchorEl(e.currentTarget);
  };

  const handleWithdrawal = (consentgroup: ProcessingConsent[]) => {
    const withdrawn = consentgroup.some((c) => c.withdrawn);
    consentgroup.forEach((c) => {
      withdrawConsent({
        variables: {
          id: c.id,
          withdrawal: !withdrawn,
        },
      });
    });
  };

  const handleShare = async (questionnaire: UserQuestionnaireApplicationTopic) => {
    try {
      await getShareToken({
        variables: {
          localQuestionnaireId: questionnaire.id,
          type: 'ListQuestionnaire',
          withConfirmation: false,
        },
      }).then(async (res) => {
        const token = res.data?.getQuestionnaireShareToken;
        if (token) {
          copyToClipboard(window.location.origin + '/share?token=' + token);
          enqueueSnackbar('Link wurde in die Zwischenablage kopiert.', { variant: 'success' });
        } else {
          enqueueSnackbar('Fehler beim Erstellen des Links.', { variant: 'error' });
        }
      });
    } catch (e) {
      enqueueSnackbar('Fehler beim Kopieren.', { variant: 'error' });
    }
  };

  const { rowProps } = useEntryHighlight();

  const columns: MUIDataTableProps['columns'] = [
    {
      name: 'index',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'description',
      label: 'Beschreibung',
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const index = tableMeta.rowData?.[0];
          const isNew = tableMeta.rowData?.[0] === 'new';
          const infoForm = infoFormData.find((i) => i.index === index);
          const originalValue = infoForm?.description || '';
          return (
            <div>
              <input
                ref={isNew ? handleRef : null}
                className="w-full bg-transparent text-sm"
                placeholder="Kurzbeschreibung"
                type="text"
                value={value}
                onChange={(e) => {
                  updateValue(e.target.value);
                }}
                onBlur={(e) => {
                  updateValue(e.target.value);
                  handleUpdate(infoForm!, e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.blur();
                    // handleUpdate(infoForm!, value);
                  }
                  // blur on escape
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.blur();
                  }
                }}
              />
            </div>
          );
        },
      },
    },
    {
      name: 'createdAt',
      label: 'Erfasst am',
      options: {
        customBodyRender: (value) => {
          return value ? new Date(value).toLocaleDateString() : '';
        },
      },
    },
    {
      name: 'questionnaire',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'actions',
      label: 'Aktionen',
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const meta = tableMeta as InfoFormTableMeta;
          const index = meta.rowData[0];
          const questionnaire = meta.rowData[3];
          const isNotFinished = (questionnaire?.progress || 0) < 1;

          const canShare = questionnaire && (principalCompany?.isDpo || principalCompany?.isAgency);
          if (isNotFinished) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Link to="#" onClick={() => handleOpenQuestionnaire(meta)}>
                  Fortsetzen
                </Link>
                {canShare && (
                  <Tooltip title="Fragebogen teilen">
                    <IconButton size="small" onClick={() => handleShare(questionnaire)}>
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            );
          } else {
            return (
              <>
                <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Anzeigen">
                  <IconButton size="small" onClick={(e) => handleShowContract(meta, e, 'show')}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Bearbeiten">
                  <IconButton size="small" onClick={(e) => handleOpenQuestionnaire(meta)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            );
          }
        },
        customHeadLabelRender: () => '',
      },
    },
    {
      name: 'delete',
      label: 'Löschen',
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const meta = tableMeta as InfoFormTableMeta;
          const index = meta.rowData[0];
          const isNew = index === 'new';

          return (
            <div className="z-30">
              <Tooltip
                enterTouchDelay={0}
                leaveTouchDelay={5000}
                title={isNew ? 'Speichern' : 'Einwilligungserklärung löschen'}
              >
                <IconButton
                  className="text-gray-500 hover:text-danger-500"
                  size="small"
                  onClick={() => {
                    if (isNew) {
                      // handleSave(description);
                    } else {
                      setDeleteRow(meta);
                      setDeleteAlertOpen(true);
                    }
                  }}
                  data-testid={`buton-action-infoForms-${meta.rowIndex}`}
                >
                  {isNew ? <SaveIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </div>
          );
        },
        customHeadLabelRender: () => '',
      },
    },
    {
      name: 'consents',
      options: {
        display: 'excluded',
      },
    },
  ];

  const options: MUIDataTableOptions = {
    setRowProps: (row) => rowProps(row[0] as string),
    sortOrder: {
      name: 'name',
      direction: 'asc',
    },
    download: false,
    print: false,
    elevation: 0,
    // onRowClick: handleClick,
    selectableRows: 'none',
    selectableRowsHeader: false,

    customToolbar: () => (
      <Button variant="outlined" onClick={handleAdd} startIcon={<AddIcon />} disabled={user?.isReadOnly} size="small">
        Neuen Datenschutzhinweis erstellen
      </Button>
    ),
    search: false,
    filter: false,
    viewColumns: false,
    pagination: false,
    textLabels: {
      selectedRows: {
        text: 'Zeilen ausgewählt',
        delete: 'Löschen',
        deleteAria: 'Ausgewählte Zeilen löschen',
      },
      body: {
        noMatch: 'Keine Einträge vorhanden',
      },
    },
  };

  return (
    <div className="mb-5">
      {infoFormData ? (
        <MUIDataTable columns={columns} data={infoFormData} title="Deine Datenschutzhinweise" options={options} />
      ) : (
        <Loading />
      )}
      <AlertDialog
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        message={
          <>
            Möchtest du den Datenschutzhinweis <b>{deleteRow?.rowData[1]}</b> wirklich löschen?
          </>
        }
        onAccept={() => deleteRow && handleDelete(deleteRow)}
      />
    </div>
  );
}

interface DocumentMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  questionnaire: UserQuestionnaireApplicationTopic | null;
}

export const DocumentMenu = ({ open, anchorEl, handleClose, questionnaire }: DocumentMenuProps): JSX.Element => {
  const documentLabels = questionnaire?.application.documentLabels;
  const { setNavigation } = useNavigation();

  const handleClick = (documentLabel: string) => {
    const index = documentLabels ? documentLabels.findIndex((label) => label === documentLabel) : -1;

    if (index < 0) return;
    questionnaire &&
      setNavigation((nav) => {
        nav.documentPaper = {
          modalOpen: true,
          questionnaire,
          index,
        };
      });
  };

  return (
    <>
      {' '}
      <Menu
        className="dtAccountMenu"
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {documentLabels?.map((dl) => (
          <MenuItem key={dl} onClick={() => handleClick(dl)}>
            {dl}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface InfoFormData {
  index: string;
  description: string;
  questionnaire?: UserQuestionnaireApplicationTopic;
  createdAt?: string;
}

const extractInfoFormData = (
  user: User | undefined,
  userValues: UserValue[],
  questionnaires: any[] | undefined
): InfoFormData[] => {
  if (!user) return [];
  const infoFormData: InfoFormData[] = [];
  userValues.forEach((userValue) => {
    if (userValue.key === 'INFOFORM_DESCRIPTION') {
      const description = userValue.value;
      const questionnaire = questionnaires?.find((q) => q.userValueIndex === userValue.index);

      infoFormData.push({
        description,
        index: userValue.index,

        questionnaire,
        createdAt: userValue.createdAt,
      });
    }
  });
  return infoFormData;
};
